import React from "react";
import PropTypes from "prop-types";

import FullScreenButton from "./buttons/FullScreenButton";
import { ShareButton } from "./buttons/ShareButton";
import { EmbedButton } from "./buttons/EmbedButton";
import { GoBackButton } from "./buttons/GoBackButton";
import { CloseStickyPlayerButton } from "./buttons/CloseStickyPlayerButton";
import { StyledTopControls, TopControlsWrapper } from "./index.stitches";
import { ButtonWrapper } from "./buttons/ButtonWrapper";
import { useVideoDataContext } from "../orca/contexts/VideoDataContext";
import { useStickyVideoContext } from "../orca/contexts/StickyVideoContext";
import {
  getCanonicalUrl,
  getRedirectUrl,
  getIsMiniPlayer,
  isExcludeVideoElement
} from "./utils";
import { useOrcaCommonContext } from "../orca/contexts/OrcaCommonContext";

export const TopControls = ({
  config,
  isVertical,
  closeCarousel,
  setSelected
}) => {
  const { videoData, videoId: contextVideoId } = useVideoDataContext();
  const { isOpenSticky } = useStickyVideoContext();
  const {
    playerSize,
    setIsFullscreen,
    isFullscreen,
    closeFullscreen,
    showFullscreenBtn,
    closeStickyPlayer
  } = useOrcaCommonContext();

  const {
    isCarousel,
    allowShare = true,
    allowEmbed = false,
    embed: embedConfig
  } = config;

  const canonicalUrl = getCanonicalUrl(videoData);
  const redirectUrl = getRedirectUrl(videoData);
  const isMiniPlayer = getIsMiniPlayer(playerSize, isFullscreen, isVertical);

  const showBackButton = (isFullscreen && isVertical) || isCarousel;
  const showCloseStickyButton = isOpenSticky && !isFullscreen;

  const isExcludeVideo = isExcludeVideoElement(videoData);
  const canShowShareableControls =
    !isMiniPlayer && (canonicalUrl || redirectUrl) && !isExcludeVideo;
  const showShareButton = canShowShareableControls && allowShare;
  const showEmbedButton =
    !isMiniPlayer && canonicalUrl && !isExcludeVideo && allowEmbed;
  const videoId = videoData?._id || contextVideoId;

  const metaHeadline = videoData?.headlines.meta_title || "";
  const basicHeadline = videoData?.headlines.basic || "";
  const headline = metaHeadline || basicHeadline;

  return (
    <TopControlsWrapper isMiniPlayer={isMiniPlayer}>
      <StyledTopControls>
        <TopLeftButton
          {...{
            showCloseStickyButton,
            closeStickyPlayer,
            showShareButton,
            showEmbedButton,
            canonicalUrl,
            redirectUrl,
            headline,
            setSelected,
            isMiniPlayer,
            videoId,
            embedConfig
          }}
        />
        <TopRightButton
          {...{
            isFullscreen,
            setIsFullscreen,
            isMiniPlayer,
            showFullscreenBtn,
            showBackButton,
            isCarousel,
            closeCarousel,
            closeFullscreen
          }}
        />
      </StyledTopControls>
    </TopControlsWrapper>
  );
};

TopControls.propTypes = {
  /** boolean flag to denote if the video is vertical */
  isVertical: PropTypes.bool,
  /** Orca config prop. */
  config: PropTypes.object,
  /** If in carousel, this callback closes carousel active state */
  closeCarousel: PropTypes.func,
  setSelected: PropTypes.func
};

export const TopLeftButton = ({
  showCloseStickyButton,
  closeStickyPlayer,
  showShareButton,
  showEmbedButton,
  canonicalUrl,
  redirectUrl,
  headline,
  setSelected,
  isMiniPlayer,
  videoId,
  embedConfig
}) => {
  return (
    <ButtonWrapper>
      {showCloseStickyButton ? (
        <CloseStickyPlayerButton closeStickyPlayer={closeStickyPlayer} />
      ) : (
        <>
          {showShareButton && (
            <ShareButton
              headline={headline}
              canonicalUrl={canonicalUrl}
              redirectUrl={redirectUrl}
              setSelected={setSelected}
              isMiniPlayer={isMiniPlayer}
            />
          )}
          <EmbedButton
            videoId={videoId}
            headline={headline}
            embedConfig={embedConfig}
            setSelected={setSelected}
            isMiniPlayer={isMiniPlayer}
            showEmbedButton={showEmbedButton}
          />
        </>
      )}
    </ButtonWrapper>
  );
};

TopLeftButton.propTypes = {
  showCloseStickyButton: PropTypes.bool,
  closeStickyPlayer: PropTypes.func,
  showShareButton: PropTypes.bool,
  showEmbedButton: PropTypes.bool,
  canonicalUrl: PropTypes.string,
  redirectUrl: PropTypes.string,
  headline: PropTypes.string,
  setSelected: PropTypes.func,
  isMiniPlayer: PropTypes.bool,
  videoId: PropTypes.string,
  embedConfig: PropTypes.object
};

export const TopRightButton = ({
  isFullscreen,
  setIsFullscreen,
  isMiniPlayer,
  showFullscreenBtn,
  showBackButton,
  isCarousel,
  closeCarousel,
  closeFullscreen
}) => {
  return (
    <ButtonWrapper>
      {showFullscreenBtn && (
        <FullScreenButton
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
          isMiniPlayer={isMiniPlayer}
        />
      )}
      {showBackButton && (
        <GoBackButton
          isCarousel={isCarousel}
          closeFullscreen={closeFullscreen}
          closeCarousel={closeCarousel}
        />
      )}
    </ButtonWrapper>
  );
};

TopRightButton.propTypes = {
  isFullscreen: PropTypes.bool,
  setIsFullscreen: PropTypes.func,
  isMiniPlayer: PropTypes.bool,
  showFullscreenBtn: PropTypes.bool,
  showBackButton: PropTypes.bool,
  isCarousel: PropTypes.bool,
  closeCarousel: PropTypes.func,
  closeFullscreen: PropTypes.func
};
